import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SleepPlan } from "@/types/sleep-plan";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, Moon, Sun, Sparkles, Calendar, Edit } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { EditSleepPlanDialog } from "./EditSleepPlanDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

interface SleepScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sleepPlan: SleepPlan | null;
  childName: string;
  isSpecialist?: boolean;
  onPlanUpdated?: () => void;
}

export function SleepScheduleDialog({
  open,
  onOpenChange,
  sleepPlan,
  childName,
  isSpecialist = false,
  onPlanUpdated,
}: SleepScheduleDialogProps) {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const { toast } = useToast();

  if (!sleepPlan?.plan_data) return null;

  const { plan_data } = sleepPlan;

  const handleSavePlan = async (updatedPlan: SleepPlan) => {
    try {
      const { error } = await supabase
        .from('sleep_plans' as any)
        .update({
          plan_data: updatedPlan.plan_data,
          manually_modified: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', updatedPlan.id);

      if (error) throw error;

      toast({
        title: "Plan updated",
        description: "Sleep plan has been successfully updated",
      });

      onPlanUpdated?.();
    } catch (error) {
      toast({
        title: "Update failed",
        description: "Failed to update sleep plan",
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Moon className="w-5 h-5 text-primary" />
                {childName}'s Sleep Schedule
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Generated on {format(new Date(sleepPlan.created_at), 'MMMM d, yyyy')}
              </p>
            </div>
            {isSpecialist && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEditDialog(true)}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Plan
              </Button>
            )}
          </div>
        </DialogHeader>

        <Tabs defaultValue="schedule" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
            <TabsTrigger value="routine">Routine</TabsTrigger>
            <TabsTrigger value="tips">Tips</TabsTrigger>
            <TabsTrigger value="goals">Goals</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[60vh] pr-4">
            {/* Daily Schedule Tab */}
            <TabsContent value="schedule" className="space-y-6 mt-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <Calendar className="w-5 h-5 text-primary" />
                  Example Daily Schedule
                </div>

                {plan_data.day_schedule.example_day.map((activity, idx) => (
                  <div
                    key={idx}
                    className="flex gap-4 p-4 rounded-lg bg-accent/50 hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-[100px]">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="font-semibold">{activity.time}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{activity.activity}</p>
                      {activity.notes && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {activity.notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))}

                {plan_data.day_schedule.personalized_note && (
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <p className="text-sm italic">{plan_data.day_schedule.personalized_note}</p>
                  </div>
                )}
              </div>

              {/* Wake Windows */}
              <div className="space-y-4 mt-8">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <Sun className="w-5 h-5 text-primary" />
                  Wake Windows
                </div>
                {plan_data.wake_windows.map((window) => (
                  <div
                    key={window.window_number}
                    className="p-4 rounded-lg bg-accent/50"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold">
                        Window {window.window_number}:
                      </span>
                      <span className="text-primary font-medium">
                        {window.duration_range}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {window.observations}
                    </p>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Routine Tab */}
            <TabsContent value="routine" className="space-y-6 mt-4">
              {/* Nap Strategy */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Moon className="w-5 h-5 text-primary" />
                  Nap Strategy
                </h3>
                <p className="text-sm text-muted-foreground">
                  {plan_data.nap_strategy.overview}
                </p>
                <ol className="space-y-2 list-decimal list-inside">
                  {plan_data.nap_strategy.steps.map((step, idx) => (
                    <li key={idx} className="text-sm">{step}</li>
                  ))}
                </ol>
                {plan_data.nap_strategy.special_considerations && (
                  <div className="mt-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <p className="text-sm font-medium mb-2">Special Considerations:</p>
                    <ul className="space-y-1">
                      {plan_data.nap_strategy.special_considerations.map((note, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground">• {note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Bedtime Routine */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Moon className="w-5 h-5 text-primary" />
                  Bedtime Routine
                </h3>
                <p className="text-sm text-muted-foreground">
                  {plan_data.bedtime_routine.overview}
                </p>
                <ol className="space-y-2 list-decimal list-inside">
                  {plan_data.bedtime_routine.steps.map((step, idx) => (
                    <li key={idx} className="text-sm">{step}</li>
                  ))}
                </ol>
              </div>

              {/* Night Wakings */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Moon className="w-5 h-5 text-primary" />
                  Night Wakings Plan
                </h3>
                <p className="text-sm text-muted-foreground">
                  {plan_data.night_wakings_plan.overview}
                </p>
                {plan_data.night_wakings_plan.first_waking && (
                  <div className="p-3 rounded-lg bg-accent/50">
                    <p className="text-sm font-medium mb-1">First Waking:</p>
                    <p className="text-sm text-muted-foreground">
                      {plan_data.night_wakings_plan.first_waking}
                    </p>
                  </div>
                )}
                {plan_data.night_wakings_plan.subsequent_wakings && (
                  <div className="p-3 rounded-lg bg-accent/50">
                    <p className="text-sm font-medium mb-1">Subsequent Wakings:</p>
                    <p className="text-sm text-muted-foreground">
                      {plan_data.night_wakings_plan.subsequent_wakings}
                    </p>
                  </div>
                )}
                {plan_data.night_wakings_plan.soothing_approaches && (
                  <div className="mt-3">
                    <p className="text-sm font-medium mb-2">Soothing Approaches:</p>
                    <ul className="space-y-1">
                      {plan_data.night_wakings_plan.soothing_approaches.map((approach, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground">• {approach}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tips Tab */}
            <TabsContent value="tips" className="space-y-4 mt-4">
              <div className="flex items-center gap-2 text-lg font-semibold mb-4">
                <Sparkles className="w-5 h-5 text-primary" />
                Daily Micro-Tips
              </div>
              <div className="grid gap-3">
                {plan_data.micro_tips.map((tip, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold bg-primary text-primary-foreground px-2 py-1 rounded">
                        Day {tip.day}
                      </span>
                      <span className="text-xs text-muted-foreground italic">
                        {tip.tone}
                      </span>
                    </div>
                    <p className="text-sm">{tip.tip}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Goals Tab */}
            <TabsContent value="goals" className="space-y-6 mt-4">
              {/* Opening Message */}
              <div className="p-4 rounded-lg bg-accent/50">
                <p className="text-sm leading-relaxed">{plan_data.personalized_opening}</p>
              </div>

              {/* Summary */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">Summary</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {plan_data.summary}
                </p>
              </div>

              {/* Goals */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">Goals</h3>
                <ul className="space-y-2">
                  {plan_data.goals.map((goal, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-primary mt-1">✓</span>
                      <span className="text-sm flex-1">{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Closing Message */}
              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm italic">{plan_data.closing_message}</p>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <EditSleepPlanDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          sleepPlan={sleepPlan}
          onSave={handleSavePlan}
        />
      </DialogContent>
    </Dialog>
  );
}
